import { db, auth, app } from "./firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  addDoc,
  limit,
  deleteDoc,
  orderBy,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";

const functions = getFunctions(app, process.env.REACT_APP_FUNCTIONS_REGION);

/**
 * A generic helper for calling any HTTPS callable Cloud Function using the Firebase SDK.
 * Handles authentication token automatically.
 * @param {string} functionName The name of the Cloud Function to call.
 * @param {object} data The data payload to send to the function.
 * @returns {Promise<any>} The result data from the function.
 */
export async function callHttpFunction(functionName, data = {}) {
  if (!auth.currentUser) {
    throw new Error("Authentication is required to call this function.");
  }

  const callableFunction = httpsCallable(functions, functionName);

  try {
    const result = await callableFunction(data);
    return result.data;
  } catch (error) {
    const code = error?.code || "unknown";
    const msg = error?.message || "Server error";
    const details = error?.details;
    console.error(`Error calling '${functionName}':`, { code, msg, details });
    const friendly = details?.friendlyMessage || msg;
    const e = new Error(friendly);
    e.code = code;
    e.details = details;
    throw e;
  }
}

// --- ALL OTHER FUNCTIONS NOW STANDARDIZED ---

export const createNewUserAccount = async (accountData) => {
  // This function is for anonymous users, so we bypass the standard auth-required helper.
  // It calls the Cloud Function directly, which is safe because the function
  // itself does not require an authenticated user to create a new account.
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const { app } = await import("./firebaseConfig");

  const functions = getFunctions(app);
  const callableFunction = httpsCallable(functions, "createNewUser");

  try {
    const result = await callableFunction(accountData);
    return result.data;
  } catch (error) {
    console.error(`Error calling function 'createNewUser':`, error);
    throw new Error(
      error.message || "An unknown error occurred during sign up.",
    );
  }
};

// This function in your original file was making a direct fetch. It's now harmonized.
export const checkInviteEligibility = async (recipientEmail) => {
  // Note: We will update the backend function to be 'onCall' in the next step.
  return callHttpFunction("checkInviteEligibility", { recipientEmail });
};

// This function for sending invites is now harmonized.
export const sendInviteEmail = async (recipientEmail) => {
  return callHttpFunction("sendInviteEmail", { recipientEmail });
};

// This function for user management is now harmonized.
export const getUsersForManagement = async () => {
  return callHttpFunction("getUsersForManagement");
};

// This function for deleting invites is now harmonized.
export const deleteUserInvite = async (inviteId) => {
  if (!inviteId) throw new Error("Invite ID is required.");
  return callHttpFunction("deleteUserInvite", { inviteId });
};

// Slice 3: Stalled Activations (SU-only).
export const getStalledActivations = async () => {
  return callHttpFunction("getStalledActivations");
};

export const resendActivationEmail = async (token) => {
  if (!token) throw new Error("Activation token is required.");
  return callHttpFunction("resendActivationEmail", { token });
};

export const deleteStalledActivation = async (token) => {
  if (!token) throw new Error("Activation token is required.");
  return callHttpFunction("deleteStalledActivation", { token });
};

// Manual backup management (SU-only). Per-project: each environment manages
// only its own bucket.
export const listBackups = async () => {
  return callHttpFunction("listBackups");
};

export const deleteBackup = async (timestamp) => {
  if (!timestamp) throw new Error("Backup timestamp is required.");
  return callHttpFunction("deleteBackup", { timestamp });
};

// SU-only provisioning. Sponsorships are the accounting/reporting layer; orgs
// carry the entitlement (gating) + a sponsorshipId pointer; assign/invite move
// users into orgs.
export const createSponsorship = async (payload) => {
  if (!payload?.name) throw new Error("Sponsorship name is required.");
  return callHttpFunction("createSponsorship", payload);
};

export const listSponsorships = async () => {
  return callHttpFunction("listSponsorships");
};

export const createOrg = async (payload) => {
  if (!payload?.name) throw new Error("Organization name is required.");
  if (!payload?.entitlement?.plan) throw new Error("Entitlement plan is required.");
  return callHttpFunction("createOrg", payload);
};

export const assignUserToOrg = async ({ email, uid, orgId, role }) => {
  if (!orgId) throw new Error("orgId is required.");
  if (!role) throw new Error("role is required.");
  if (!email && !uid) throw new Error("email or uid is required.");
  return callHttpFunction("assignUserToOrg", { email, uid, orgId, role });
};

export const inviteUserToOrg = async ({ email, orgId }) => {
  if (!email) throw new Error("email is required.");
  if (!orgId) throw new Error("orgId is required.");
  return callHttpFunction("inviteUserToOrg", { email, orgId });
};

// Returns ALL organizations (managed, self-service, and the free default) with
// member counts + nullable entitlement fields, for the SU provisioning table.
export const listOrgs = async () => {
  return callHttpFunction("listOrgs");
};

// SU-only edit/delete (tranche 2). updateSponsorship/deleteSponsorship edit the
// accounting layer; updateOrg edits org + entitlement + sponsorship link;
// findUser/updateUser support the user editor (no email edit this tranche).
export const updateSponsorship = async (payload) => {
  if (!payload?.sponsorshipId) throw new Error("sponsorshipId is required.");
  if (!payload?.name) throw new Error("Sponsorship name is required.");
  return callHttpFunction("updateSponsorship", payload);
};

export const deleteSponsorship = async (sponsorshipId) => {
  if (!sponsorshipId) throw new Error("sponsorshipId is required.");
  return callHttpFunction("deleteSponsorship", { sponsorshipId });
};

export const updateOrg = async (payload) => {
  if (!payload?.orgId) throw new Error("orgId is required.");
  if (!payload?.name) throw new Error("Organization name is required.");
  if (!payload?.entitlement?.plan) throw new Error("Entitlement plan is required.");
  return callHttpFunction("updateOrg", payload);
};

export const findUser = async (email) => {
  if (!email) throw new Error("email is required.");
  return callHttpFunction("findUser", { email });
};

export const updateUser = async (payload) => {
  if (!payload?.uid) throw new Error("uid is required.");
  return callHttpFunction("updateUser", payload);
};

// --- All other functions from your file remain unchanged as they were already correct ---

export const sendPasswordReset = async (email) => {
  await sendPasswordResetEmail(auth, email);
};

export const getAllOrganizations = async () => {
  try {
    const orgsRef = collection(db, "organizations");
    const snapshot = await getDocs(orgsRef);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching all organizations:", error);
    throw error;
  }
};

export const getOrganizationDetails = async (orgId) => {
  if (!orgId) return null;
  const orgDocRef = doc(db, "organizations", orgId);
  const orgDocSnap = await getDoc(orgDocRef);
  return orgDocSnap.exists() ? orgDocSnap.data() : null;
};

// --- User Profile ---
export const getUserProfile = async (uid) => {
  if (!uid) return null;
  try {
    const userDocRef = doc(db, "users", uid);
    const userDocSnap = await getDoc(userDocRef);
    return userDocSnap.exists() ? userDocSnap.data() : null;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

// --- User Management ---
export const getActiveUsers = async (organizationId = null) => {
  try {
    const constraints = [where("status", "==", "active")];
    if (organizationId) {
      constraints.push(where("organizationId", "==", organizationId));
    }
    const q = query(collection(db, "users"), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching active users:", error);
    throw error;
  }
};

export const getInactiveUsers = async (organizationId = null) => {
  try {
    const constraints = [where("status", "==", "inactive")];
    if (organizationId) {
      constraints.push(where("organizationId", "==", organizationId));
    }
    const q = query(collection(db, "users"), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching inactive users:", error);
    throw error;
  }
};

export const getPendingActivationUsers = async (organizationId = null) => {
  try {
    const constraints = [where("status", "==", "pending_activation")];
    if (organizationId) {
      constraints.push(where("organizationId", "==", organizationId));
    }
    const q = query(collection(db, "users"), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching pending activation users:", error);
    throw error;
  }
};

export const promoteUserToAdmin = async (uid) => {
  const userDocRef = doc(db, "users", uid);
  await updateDoc(userDocRef, { role: "admin" });
};

export const deactivateUser = async (uid) => {
  const userDocRef = doc(db, "users", uid);
  await updateDoc(userDocRef, { status: "inactive" });
};

export const reactivateUser = async (uid) => {
  const userDocRef = doc(db, "users", uid);
  await updateDoc(userDocRef, { status: "active", role: "user" });
};

export const activateNewAdmin = async () => {
  return callHttpFunction("activateNewAdmin");
};

export const isEmailInUse = async (email) => {
  if (!email) return false;
  const q = query(collection(db, "users"), where("email", "==", email));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

// --- Test/Case Management ---
export const getAllTestsByOrg = async (organizationId) => {
  if (!organizationId) throw new Error("Organization ID is required.");
  try {
    const testsRef = collection(db, "tests");
    const q = query(testsRef, where("organizationId", "==", organizationId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error getting all tests:", error);
    throw error;
  }
};

export const getTestResultById = async (testId) => {
  if (!testId) throw new Error("Test ID required.");
  try {
    const testDocRef = doc(db, "tests", testId);
    const testDocSnap = await getDoc(testDocRef);
    return testDocSnap.exists()
      ? { id: testDocSnap.id, ...testDocSnap.data() }
      : null;
  } catch (error) {
    console.error("Error fetching test result by ID:", error);
    throw error;
  }
};

export const updateTestResult = async (testId, data) => {
  const testDocRef = doc(db, "tests", testId);
  await updateDoc(testDocRef, data);
};

export const getAllTests = async () => {
  try {
    const testsRef = collection(db, "tests");
    const querySnapshot = await getDocs(testsRef);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error getting all tests:", error);
    throw error;
  }
};

export const getTestsForUser = async (userId) => {
  if (!userId) return [];
  try {
    const testsRef = collection(db, "tests");
    const q = query(testsRef, where("submitterUid", "==", userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error getting tests for user:", error);
    throw error;
  }
};

// --- Correction Request Management ---
export const addCorrectionRequest = async ({
  testId,
  organizationId,
  reason,
  requestedByUid,
}) => {
  if (!testId || !organizationId || !reason || !requestedByUid) {
    throw new Error("Missing required fields for correction request.");
  }
  try {
    const requestsRef = collection(db, "correctionRequests");
    await addDoc(requestsRef, {
      testId,
      organizationId,
      reason,
      requestedByUid,
      status: "open",
      requestedAt: new Date(),
    });
  } catch (error) {
    console.error("Error adding correction request:", error);
    throw error;
  }
};

export const getOpenCorrectionRequests = async (organizationId = null) => {
  try {
    const constraints = [where("status", "==", "open")];
    if (organizationId) {
      constraints.push(where("organizationId", "==", organizationId));
    }
    const q = query(collection(db, "correctionRequests"), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching open correction requests:", error);
    throw error;
  }
};

export const getOpenRequestForTest = async (testId, organizationId) => {
  if (!testId || !organizationId) {
    throw new Error("Test ID and Organization ID are required.");
  }
  try {
    const q = query(
      collection(db, "correctionRequests"),
      where("testId", "==", testId),
      where("organizationId", "==", organizationId),
      where("status", "==", "open"),
      limit(1),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    const docSnap = snapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error("Error fetching open request for test:", error);
    throw error;
  }
};

export const hasOpenRequest = async (testId, organizationId) => {
  const request = await getOpenRequestForTest(testId, organizationId);
  return !!request;
};

export const resolveCorrectionRequest = async (requestId) => {
  if (!requestId) throw new Error("Request ID is required.");
  const requestDocRef = doc(db, "correctionRequests", requestId);
  await updateDoc(requestDocRef, {
    status: "resolved",
    resolvedAt: new Date(),
    resolvedBy: auth.currentUser.uid,
  });
};

// --- START: SUPERUSER FUNCTIONS ---

// Case-insensitive uniqueness check on an org doc field. Routes through a
// Cloud Function callable (server-side admin SDK) so it works for the
// unauthenticated signup-page caller. App Check provides abuse protection.
export const isOrgFieldUnique = async (field, value) => {
  if (!value) return false;
  if (field !== "name" && field !== "shortName") {
    throw new Error(`isOrgFieldUnique: unsupported field "${field}"`);
  }
  const trimmed = String(value).trim();
  if (!trimmed) return false;
  const callable = httpsCallable(functions, "isOrgFieldUnique");
  try {
    const result = await callable({ field, value: trimmed });
    return Boolean(result.data?.isUnique);
  } catch (err) {
    console.error("isOrgFieldUnique failed:", err);
    throw err;
  }
};

export const planCleanup = async (
  options = { includeStorage: true, maxSamples: 10 },
) => {
  return callHttpFunction("cleanupTestData", { mode: "plan", options });
};

export const executeCleanup = async ({
  plan,
  options = { maxDeletes: 100000, preserveKeeperOrgTrees: true },
}) => {
  return callHttpFunction("cleanupTestData", {
    mode: "execute",
    plan,
    options,
  });
};

export async function sendTestEmail(opts) {
  const fn = httpsCallable(functions, "sendTestEmail");
  const payload =
    typeof opts === "string" ? { targetEmail: opts } : opts || {};
  const result = await fn(payload);
  return result.data;
}

/**
 * Unified delete user/data callable — supports both plan and execute modes.
 * @param {object} params
 * @param {'plan'|'delete'} params.mode  - Use 'plan' for dry-run, 'delete' for actual deletion
 * @param {string} params.email          - Email to identify user (required)
 * @param {object} [params.plan]         - Plan object (required for delete)
 * @param {object} [params.options]      - Options like { hardDelete: true }
 */
export const deleteUserAndData = async ({
  mode,
  email,
  plan,
  options = {},
}) => {
  if (!mode || !email) throw new Error("mode and email are required.");
  return callHttpFunction("deleteUserAndData", { mode, email, plan, options });
};

// --- END: SUPERUSER FUNCTIONS ---

// ============================================================
// PHASE 5A: AMR List and Custom Breakpoint Functions
// ============================================================

// --- AMR List Fetching ---

/**
 * Get antibiotics that have breakpoints for a given track
 * @param {string} track - "human" or "vet"
 * @returns {Promise<Array>} List of antibiotics with breakpoints
 */
export const getAntibioticList = async (track) => {
  try {
    const listRef = collection(db, "amr_antibiotic_list");
    const q = query(listRef, where("track", "==", track));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching antibiotic list:", error);
    throw error;
  }
};

/**
 * Get all antimicrobials from the AMR package (~500)
 * @returns {Promise<Array>} Full list of antimicrobials
 */
export const getAllAntimicrobials = async () => {
  try {
    const ref = collection(db, "amr_antimicrobials");
    const snapshot = await getDocs(ref);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching all antimicrobials:", error);
    throw error;
  }
};

/**
 * Get organisms with breakpoints
 * @returns {Promise<Array>} List of organisms
 */
export const getOrganismList = async () => {
  try {
    const ref = collection(db, "amr_organisms");
    const snapshot = await getDocs(ref);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching organism list:", error);
    throw error;
  }
};

// --- Organization Custom Lists (Phase 5) ---

/**
 * Get enabled antibiotics for an organization
 * @param {string} organizationId
 * @returns {Promise<Array>} List of enabled antibiotics
 */
export const getEnabledAntibiotics = async (organizationId) => {
  if (!organizationId) throw new Error("Organization ID is required.");
  try {
    const ref = collection(
      db,
      "organizations",
      organizationId,
      "enabled_antibiotics",
    );
    const snapshot = await getDocs(ref);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching enabled antibiotics:", error);
    throw error;
  }
};

/**
 * Add an enabled antibiotic for an organization
 * @param {string} organizationId
 * @param {object} data - { ab, abname, concentration, enabledBy, enabledAt, active }
 */
export const addEnabledAntibiotic = async (organizationId, data) => {
  if (!organizationId) throw new Error("Organization ID is required.");
  if (!data.ab) throw new Error("Antibiotic code is required.");

  try {
    const ref = collection(
      db,
      "organizations",
      organizationId,
      "enabled_antibiotics",
    );
    await addDoc(ref, {
      ...data,
      enabledAt: data.enabledAt || new Date(),
      enabledBy: data.enabledBy || auth.currentUser?.uid,
      active: true,
    });
  } catch (error) {
    console.error("Error adding enabled antibiotic:", error);
    throw error;
  }
};

/**
 * Remove (delete) an enabled antibiotic
 * @param {string} organizationId
 * @param {string} docId
 */
export const removeEnabledAntibiotic = async (organizationId, docId) => {
  if (!organizationId || !docId)
    throw new Error("Organization ID and document ID are required.");

  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "enabled_antibiotics",
      docId,
    );
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error removing enabled antibiotic:", error);
    throw error;
  }
};

/**
 * Fetch all entries from the amrie_antibiotics collection.
 * Returns the canonical drug list used by the AMRIE pipeline (~141 entries).
 * Document IDs are WHONET drug codes (e.g. "CIP", "AMP").
 *
 * Field shape on disk uses SCREAMING_SNAKE_CASE per the WHONET data
 * convention; this fetcher maps them to camelCase JS-side for ergonomics.
 */
export const getAmrieAntibiotics = async () => {
  try {
    const ref = collection(db, "amrie_antibiotics");
    const snapshot = await getDocs(ref);
    return snapshot.docs
      .map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          whonetAbxCode: d.WHONET_ABX_CODE || doc.id,
          antibiotic: d.ANTIBIOTIC || "",
          drugClass: d.CLASS || "",
          subclass: d.SUBCLASS || null,
          atcCode: d.ATC_CODE || null,
          whoAware: d.WHO_AWARE || null,
          // Pre-aggregated list of disk potencies for this drug,
          // built by amrie_transform.py from AMRIE's Antibiotics.txt.
          // Same source as the Android AssessDiskScreen reads via
          // AmrieDataRepository — keeps the two clients aligned on
          // what disk strengths exist for each drug.
          potencies: Array.isArray(d.POTENCIES) ? d.POTENCIES : [],
        };
      })
      .filter((a) => a.whonetAbxCode)
      .sort((a, b) => a.antibiotic.localeCompare(b.antibiotic));
  } catch (error) {
    console.error("Error fetching amrie_antibiotics:", error);
    throw error;
  }
};

/**
 * Fetch all entries from the amrie_organisms collection.
 * Returns the species-level organism list (~2350 entries).
 * Document IDs are WHONET organism codes (e.g. "eco", "sau").
 *
 * NOTE: This returns species-level entries only. Family/genus-level
 * codes (e.g. "EBC" for Enterobacterales, "STA" for Staphylococcus)
 * are also valid for breakpoint lookup but are NOT in this collection
 * — they would have to come from amrie_breakpoints if family/genus-level
 * custom rules are ever supported.
 */
export const getAmrieOrganisms = async () => {
  try {
    const ref = collection(db, "amrie_organisms");
    const snapshot = await getDocs(ref);
    return snapshot.docs
      .map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          whonetOrgCode: d.WHONET_ORG_CODE || doc.id,
          organism: d.ORGANISM || "",
          // Full AMRIE hierarchy — needed for the
          // buildOrganismMatchTuples helper that drives clinical
          // indication cascade (see EditTest cascade utils).
          serovarGroup: d.SEROVAR_GROUP || null,
          speciesGroup: d.SPECIES_GROUP || null,
          genusCode: d.GENUS_CODE || null,
          genusGroup: d.GENUS_GROUP || null,
          familyCode: d.FAMILY_CODE || null,
          anaerobe: d.ANAEROBE || null, // "X" if anaerobe
          subkingdomCode: d.SUBKINGDOM_CODE || null, // "+" or "-"
        };
      })
      .filter((o) => o.whonetOrgCode && o.organism)
      .sort((a, b) => a.organism.localeCompare(b.organism));
  } catch (error) {
    console.error("Error fetching amrie_organisms:", error);
    throw error;
  }
};

/**
 * Fetch all entries from the specimen_type_master collection.
 * Returns the WHONET specimen-type list (~190 entries: blood "bl",
 * urine "ur", stool "st", etc.).
 *
 * Document IDs are WHONET codes; field shape:
 *   code: "bl"
 *   display_name: "Blood"
 *   translations: { ru: "Кровь", tg: "Хун", ... }
 *
 * Used by reports (PrintReport, ViewTest) to convert the persisted code
 * back to a readable label. The app's metadata screen stores the code
 * (for archival consistency), so reports must look it up at display time.
 */
export const getSpecimenTypes = async () => {
  try {
    const ref = collection(db, "specimen_type_master");
    const snapshot = await getDocs(ref);
    return snapshot.docs
      .map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          code: d.code || doc.id,
          displayName: d.display_name || doc.id,
          translations: d.translations || {},
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  } catch (error) {
    console.error("Error fetching specimen_type_master:", error);
    throw error;
  }
};

/**
 * Fetch all entries from the clinical_indication_master collection.
 * Returns the AMRIE site-of-infection labels with translations (32 entries
 * — 31 distinct AMRIE SITE_OF_INFECTION values plus the synthetic
 * "General / Not specified" wildcard).
 *
 * Document IDs are snake_case codes; field shape:
 *   code: "uncomplicated_uti"
 *   display_name: "Uncomplicated urinary tract infection"
 *   translations: { ru: "...", tg: "...", ... }
 *
 * The display_name is the lookup key — it matches the SITE_OF_INFECTION
 * string in amrie_breakpoints exactly, so clients can map a stored English
 * indication value (e.g. snapshot.clinicalIndication) to a localized label.
 *
 * Used by EditTest, ViewTest, CustomLists, and PrintReport. The data layer
 * stores the English display string (per the English-only storage policy),
 * not the code; this lookup is consulted at display time only.
 */
export const getClinicalIndications = async () => {
  try {
    const ref = collection(db, "clinical_indication_master");
    const snapshot = await getDocs(ref);
    return snapshot.docs
      .map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          code: d.code || doc.id,
          displayName: d.display_name || doc.id,
          translations: d.translations || {},
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  } catch (error) {
    console.error("Error fetching clinical_indication_master:", error);
    throw error;
  }
};

/**
 * Fetch all entries from the species_master collection.
 * Returns the host/animal-species list used by the metadata screen
 * (~10-15 entries: Human, Cattle, Dogs, Cats, etc.).
 *
 * Sorted Human-first then alphabetical to match the Android dropdown.
 */
export const getSpeciesMaster = async () => {
  try {
    const ref = collection(db, "species_master");
    const snapshot = await getDocs(ref);
    return snapshot.docs
      .map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          code: d.code || doc.id,
          displayName: d.display_name || doc.id,
          // Localized labels keyed by language code (e.g. ru, tg).
          // Display layer falls back to displayName when no entry exists.
          translations: d.translations || {},
          track: d.track || "vet",
          source: d.source || "amrie",
          amrieHost: d.amrie_host || null,
          // Multi-host case: e.g. "Horses" maps to
          // ["Horses", "Horses (adults)", "Horses (foals)"].
          // The interpretation cascade tries each in priority order.
          amrieHostList: Array.isArray(d.amrie_host_list)
            ? d.amrie_host_list
            : [],
        };
      })
      .sort((a, b) => {
        if (a.track === "human" && b.track !== "human") return -1;
        if (b.track === "human" && a.track !== "human") return 1;
        return a.displayName.localeCompare(b.displayName);
      });
  } catch (error) {
    console.error("Error fetching species_master:", error);
    throw error;
  }
};

/**
 * Fetch all entries from the amrie_breakpoints collection.
 * Returns ~2200 breakpoint rules (current year, CLSI+EUCAST, DISK only).
 *
 * Used by CustomLists for cascading dropdowns:
 *   - Potency options for a drug = distinct potencies in this list for that drug
 *   - Clinical indication options for organism+host = distinct
 *     SITE_OF_INFECTION values in this list for that organism+host
 *
 * One large fetch; cache in component state for the session.
 */
export const getAmrieBreakpoints = async () => {
  try {
    const ref = collection(db, "amrie_breakpoints");
    const snapshot = await getDocs(ref);
    return snapshot.docs.map((doc) => {
      const d = doc.data();
      return {
        id: doc.id,
        guidelines: d.GUIDELINES || "",
        year: d.YEAR || "",
        whonetAbxCode: d.WHONET_ABX_CODE || "",
        potency: d.POTENCY || null,
        organismCode: d.ORGANISM_CODE || "",
        organismCodeType: d.ORGANISM_CODE_TYPE || "",
        host: d.HOST || null,
        siteOfInfection: d.SITE_OF_INFECTION || null,
        r: d.R ?? null,
        s: d.S ?? null,
      };
    });
  } catch (error) {
    console.error("Error fetching amrie_breakpoints:", error);
    throw error;
  }
};

/**
 * Get custom breakpoints for an organization
 * @param {string} organizationId
 * @returns {Promise<Array>} List of custom breakpoints
 */
export const getCustomBreakpoints = async (organizationId) => {
  if (!organizationId) throw new Error("Organization ID is required.");
  try {
    const ref = collection(
      db,
      "organizations",
      organizationId,
      "custom_breakpoints",
    );
    const snapshot = await getDocs(ref);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching custom breakpoints:", error);
    throw error;
  }
};

/**
 * Add a custom breakpoint for an organization
 * @param {string} organizationId
 * @param {object} data - Breakpoint data
 */
export const addCustomBreakpoint = async (organizationId, data) => {
  if (!organizationId) {
    throw new Error("Organization ID is required.");
  }

  // Required fields per the new schema. The form-side validation in
  // CustomLists.js gates the Save button on these too, but server-side
  // validation here is the second line of defence.
  const required = [
    "whonetAbxCode",
    "potency",
    "organismCode",
    "organismCodeType",
    "host",
    "s",
    "r",
  ];
  for (const f of required) {
    if (data[f] === undefined || data[f] === null || data[f] === "") {
      throw new Error(`Custom breakpoint is missing required field: ${f}`);
    }
  }

  // Numeric sanity: S and R must be positive integers, S > R
  const s = Number(data.s);
  const r = Number(data.r);
  if (!Number.isInteger(s) || !Number.isInteger(r)) {
    throw new Error("S and R must both be integers");
  }
  if (s <= r) {
    throw new Error("S threshold must be greater than R threshold");
  }

  try {
    const ref = collection(
      db,
      "organizations",
      organizationId,
      "custom_breakpoints",
    );
    await addDoc(ref, {
      // The canonical camelCase shape — matches Android's OrgCustomBreakpoint
      whonetAbxCode: data.whonetAbxCode,
      potency: data.potency,
      organismCode: data.organismCode,
      organismCodeType: data.organismCodeType,
      host: data.host,
      siteOfInfection: data.siteOfInfection ?? null,
      s: s,
      r: r,
      sourceLabel: data.sourceLabel ?? "",
      rationale: data.rationale ?? null,
      active: true,
      createdAt: new Date(),
      createdBy: auth.currentUser?.uid || null,
    });
  } catch (error) {
    console.error("Error adding custom breakpoint:", error);
    throw error;
  }
};

/**
 * Update a custom breakpoint
 * @param {string} organizationId
 * @param {string} docId
 * @param {object} data - Updated breakpoint data
 */
export const updateCustomBreakpoint = async (organizationId, docId, data) => {
  if (!organizationId || !docId)
    throw new Error("Organization ID and document ID are required.");

  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "custom_breakpoints",
      docId,
    );
    await updateDoc(docRef, {
      ...data,
      updatedAt: new Date(),
      updatedBy: auth.currentUser?.uid,
    });
  } catch (error) {
    console.error("Error updating custom breakpoint:", error);
    throw error;
  }
};

/**
 * Deactivate (soft delete) a custom breakpoint
 * @param {string} organizationId
 * @param {string} docId
 * @param {string} userId - User performing the deactivation
 */
export const deactivateCustomBreakpoint = async (
  organizationId,
  docId,
  userId,
) => {
  if (!organizationId || !docId)
    throw new Error("Organization ID and document ID are required.");

  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "custom_breakpoints",
      docId,
    );
    await updateDoc(docRef, {
      active: false,
      deactivatedAt: new Date(),
      deactivatedBy: userId || auth.currentUser?.uid,
    });
  } catch (error) {
    console.error("Error deactivating custom breakpoint:", error);
    throw error;
  }
};

/**
 * Set the active flag on a custom breakpoint (toggle on or off).
 * Soft-only — the doc is preserved, only the active field flips.
 * Tracks who toggled it and when via deactivatedAt/By or
 * reactivatedAt/By, depending on direction.
 *
 * @param {string} organizationId
 * @param {string} docId
 * @param {boolean} isActive - target state
 * @param {string} userId - User performing the toggle (optional;
 *                          defaults to auth.currentUser.uid)
 */
export const setCustomBreakpointActive = async (
  organizationId,
  docId,
  isActive,
  userId,
) => {
  if (!organizationId || !docId)
    throw new Error("Organization ID and document ID are required.");

  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "custom_breakpoints",
      docId,
    );
    const uid = userId || auth.currentUser?.uid;
    const now = new Date();
    if (isActive) {
      await updateDoc(docRef, {
        active: true,
        reactivatedAt: now,
        reactivatedBy: uid,
      });
    } else {
      await updateDoc(docRef, {
        active: false,
        deactivatedAt: now,
        deactivatedBy: uid,
      });
    }
  } catch (error) {
    console.error("Error toggling custom breakpoint active state:", error);
    throw error;
  }
};
/**
 * Get antibiotics from BOTH human and vet tracks (deduplicated)
 * Used for filtering eligible antibiotics to enable
 * @returns {Promise<Array>} Combined list of antibiotics with breakpoints
 */
export const getAntibioticListBothTracks = async () => {
  try {
    const listRef = collection(db, "amr_antibiotic_list");
    const snapshot = await getDocs(listRef);

    // Deduplicate by ab code (in case same antibiotic exists in both tracks)
    const abMap = new Map();
    snapshot.docs.forEach((doc) => {
      const data = { id: doc.id, ...doc.data() };
      if (!abMap.has(data.ab)) {
        abMap.set(data.ab, data);
      }
    });

    return Array.from(abMap.values());
  } catch (error) {
    console.error("Error fetching antibiotic list (both tracks):", error);
    throw error;
  }
};

/**
 * Get master breakpoints with optional track filtering (client-side)
 * @param {object} options
 * @param {string} [options.track] - Filter by track ("human" or "vet")
 * @param {number} [options.pageSize=100] - Number of results to return
 * @returns {Promise<{breakpoints: Array, hasMore: boolean}>}
 */
export const getMasterBreakpoints = async (options = {}) => {
  try {
    const { track = null } = options;

    const ref = collection(db, "master_breakpoints");

    // Query with track filter on server side
    let q;
    if (track) {
      q = query(ref, where("track", "==", track));
    } else {
      q = query(ref);
    }

    const snapshot = await getDocs(q);

    let results = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Sort client-side by antibiotic code, then organism
    results.sort((a, b) => {
      const abCompare = (a.ab || "").localeCompare(b.ab || "");
      if (abCompare !== 0) return abCompare;
      return (a.mo || "").localeCompare(b.mo || "");
    });

    return results;
  } catch (error) {
    console.error("Error fetching master breakpoints:", error);
    throw error;
  }
};

/**
 * Search master breakpoints by antibiotic or organism
 * This is a client-side filter after fetching since Firestore doesn't support
 * partial string matching. For large datasets, consider using a Cloud Function.
 * @param {object} options
 * @param {string} [options.track] - Filter by track
 * @param {string} [options.searchTerm] - Search term for ab or mo or their names
 * @param {number} [options.maxResults=100] - Maximum results to return
 * @returns {Promise<Array>} Filtered breakpoints
 */
export const searchMasterBreakpoints = async (options = {}) => {
  try {
    const { track = null, searchTerm = "", maxResults = 100 } = options;

    const ref = collection(db, "master_breakpoints");

    // Build query - filter by track if specified
    const constraints = [];
    if (track) {
      constraints.push(where("track", "==", track));
    }

    // Limit to a reasonable number to filter client-side
    constraints.push(limit(2000));

    const q =
      constraints.length > 0
        ? query(ref, ...constraints)
        : query(ref, limit(2000));

    const snapshot = await getDocs(q);

    let results = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Client-side filtering if search term provided
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      results = results.filter(
        (bp) =>
          bp.ab?.toLowerCase().includes(term) ||
          bp.mo?.toLowerCase().includes(term),
      );
    }

    // Limit results
    return results.slice(0, maxResults);
  } catch (error) {
    console.error("Error searching master breakpoints:", error);
    throw error;
  }
};

// ---------------------------------------------------------------------
// Revisions (Phase C versioning model)
// ---------------------------------------------------------------------
//
// Every test record has an immutable revisions subcollection. Initial
// submission writes revision 1. Each subsequent edit appends a new
// revision and updates the parent doc's denormalized current-state
// fields. Past revisions are never mutated.
//
// Read paths (PrintReport, ViewTest, FindTest, Reports) continue to
// read top-level test fields directly; the revisions subcollection is
// only consulted by the EditTest flow and the (future) HistoryView page.

/**
 * Fetch the current revision document for a test.
 * Reads the parent's `currentRevision` field, then reads
 * `tests/{testId}/revisions/{currentRevision}`.
 *
 * Returns the revision doc data (including the embedded `snapshot` field)
 * with an added `id` property, or null if either doc is missing.
 *
 * Used by EditTest as the "starting point" for editing — the snapshot
 * inside is what the editor sees as the current state.
 */
export const getCurrentRevision = async (testId) => {
  if (!testId) throw new Error("testId is required.");
  try {
    const parentRef = doc(db, "tests", testId);
    const parentSnap = await getDoc(parentRef);
    if (!parentSnap.exists()) return null;
    const parent = parentSnap.data();
    const currentRev = parent.currentRevision || 1;
    const revRef = doc(db, "tests", testId, "revisions", String(currentRev));
    const revSnap = await getDoc(revRef);
    if (!revSnap.exists()) return null;
    return { id: revSnap.id, ...revSnap.data() };
  } catch (error) {
    console.error("Error fetching current revision:", error);
    throw error;
  }
};

/**
 * Fetch ALL revisions for a test, ordered by revisionNumber ascending.
 * Used by the (future) HistoryView page and for diff displays.
 */
export const getAllRevisions = async (testId) => {
  if (!testId) throw new Error("testId is required.");
  try {
    const revsRef = collection(db, "tests", testId, "revisions");
    const q = query(revsRef, orderBy("revisionNumber", "asc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Error fetching revisions:", error);
    throw error;
  }
};

/**
 * Append a new revision and update the parent test doc atomically.
 *
 * Args:
 *   testId           — parent test document ID
 *   newSnapshot      — full case data to embed as the revision's snapshot
 *                      (and to denormalize onto the parent doc)
 *   editorUid        — the editing user's UID
 *   changeReason     — why this edit was made (mandatory; surfaced in
 *                      reports' revision history)
 *
 * Behavior:
 *   1. Reads the parent doc to determine the next revision number.
 *   2. Writes BOTH the new revision doc AND updates the parent
 *      atomically via a Firestore writeBatch.
 *   3. Parent's denormalized fields are replaced wholesale with the
 *      new snapshot, plus revision-tracking metadata is updated.
 *
 * Returns:
 *   { revisionNumber, ... } — the new revision metadata.
 *
 * Throws:
 *   If the parent doc doesn't exist, or any part of the write fails.
 *
 * Used by EditTest's Save action.
 */
export const addRevision = async ({
  testId,
  newSnapshot,
  editorUid,
  changeReason,
}) => {
  if (!testId) throw new Error("testId is required.");
  if (!newSnapshot) throw new Error("newSnapshot is required.");
  if (!editorUid) throw new Error("editorUid is required.");
  if (!changeReason || !changeReason.trim()) {
    throw new Error("changeReason is required for an edit.");
  }

  const parentRef = doc(db, "tests", testId);
  const parentSnap = await getDoc(parentRef);
  if (!parentSnap.exists()) {
    throw new Error(`Test ${testId} not found.`);
  }
  const parent = parentSnap.data();
  const previousRevision = parent.currentRevision || 1;
  const newRevision = previousRevision + 1;
  const newRevisionRef = doc(
    db,
    "tests",
    testId,
    "revisions",
    String(newRevision),
  );

  // Build the parent-doc update — replace ALL case-data fields with the
  // new snapshot, then add the revision-tracking metadata.
  const parentUpdate = {
    ...newSnapshot,
    lastModifiedAt: serverTimestamp(),
    lastEditedAt: serverTimestamp(), // legacy compat with watermarkOnEdit trigger
    lastEditedByUid: editorUid,
    isEdited: true,
    currentRevision: newRevision,
    revisionCount: newRevision,
    editReason: changeReason.trim(),
  };

  const newRevisionDoc = {
    revisionNumber: newRevision,
    createdAt: serverTimestamp(),
    createdByUid: editorUid,
    changeReason: changeReason.trim(),
    supersedesRevisionNumber: previousRevision,
    // Denormalized for security rules — see DataRepository.kt for rationale.
    organizationId: parent.organizationId,
    snapshot: newSnapshot,
  };

  const batch = writeBatch(db);
  batch.update(parentRef, parentUpdate);
  batch.set(newRevisionRef, newRevisionDoc);
  await batch.commit();

  return {
    revisionNumber: newRevision,
    supersedesRevisionNumber: previousRevision,
  };
};
